import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import CommonLayout from "../../../layouts/CommonLayout";
import { useNavigate } from "react-router-dom";
import { ROUTE_CONSTANT } from "../../../routes/constant";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import EditNewsModal from "../../../components/Model/EditNewsModel";
import { extractObjectId } from "../../../utils/utils";
import { toast } from "react-toastify";
import {
  FaNewspaper,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaStar,
  FaUser,
  FaFileAlt,
} from "react-icons/fa";
import './NewsPage.css';

function NewsPage() {
  const [newsList, setNewsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newsToDelete, setNewsToDelete] = useState(null);
  const [showNewsEditModal, setNewsEditModal] = useState(false);
  const [newsEditData, setNewsEditData] = useState({});

  const navigate = useNavigate();

  const fetchNews = async () => {
    try {
      const response = await allAPiServicesCall.getNews(
        {},
        getAuthConfig(),
        navigate
      );
      setNewsList(response.data);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToNewsDetails = (id) => {
    navigate(`/news-details/${id}`);
  };

  const confirmDelete = (id) => {
    setNewsToDelete(id);
    setShowDeleteModal(true);
  };

  const handleOnDeleteNews = async () => {
    if (!newsToDelete) return;
    setIsLoading(true);
    try {
      let payload = { newsId: newsToDelete };
      await allAPiServicesCall.deleteNews(
        payload,
        getAuthConfig(),
        navigate
      );

      setShowDeleteModal(false);
      fetchNews();
    } catch (error) {
      console.log(error, ":error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const setNewsPriority = (NewsId) => {
    setNewsEditModal(true);
    setNewsEditData(NewsId);
  };

  const handleHeroBannerToggle = async (newsId, currentValue) => {
    try {
      const newsIdStr = extractObjectId(newsId);

      if (!newsIdStr || newsIdStr === '[object Object]') {
        console.error('Invalid newsId:', newsId);
        alert("Invalid news ID. Please refresh and try again.");
        return;
      }

      if (currentValue) {
        toast.info(
          "This article is already featured on the homepage. To clear or replace it, use Page Management or choose a different article.",
        );
        return;
      }

      setIsLoading(true);
      await allAPiServicesCall.savePageConfig(
        {
          pageType: 'homepage',
          hero: {
            explicitNewsArticleId: newsIdStr,
          },
        },
        getAuthConfig(),
        navigate,
      );

      await fetchNews();
    } catch (error) {
      console.error("Error updating hero banner status:", error);
      alert("Failed to update hero banner. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const stripHtml = (html) => {
    return html?.replace(/<\/?[^>]+(>|$)/g, "") || "";
  };

  return (
    <CommonLayout>
      <div className="news-page">
        {/* Header Section */}
        <div className="news-header">
          <div className="news-header-content">
            <div className="news-header-icon">
              <FaNewspaper />
            </div>
            <div className="news-header-text">
              <h1 className="news-title">News Management</h1>
              <p className="news-subtitle">
                Create, edit, and manage news articles
              </p>
            </div>
          </div>
          <div className="news-stats">
            <div className="news-stat-badge">
              <FaFileAlt />
              <span><strong>{newsList?.length || 0}</strong> Articles</span>
            </div>
          </div>
          <button
            className="news-add-btn"
            onClick={() => navigate(ROUTE_CONSTANT.COMMON.ADD_NEWS)}
          >
            <FaPlus /> Add News
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="news-loading">
            <div className="news-loading-spinner"></div>
            <p className="news-loading-text">Loading news...</p>
          </div>
        ) : newsList?.length === 0 ? (
          <div className="news-empty">
            <div className="news-empty-icon">
              <FaNewspaper />
            </div>
            <p className="news-empty-text">No news articles found</p>
            <button
              className="news-add-btn"
              onClick={() => navigate(ROUTE_CONSTANT.COMMON.ADD_NEWS)}
            >
              <FaPlus /> Create First Article
            </button>
          </div>
        ) : (
          <div className="news-grid">
            {newsList?.map((news) => {
              const _id = extractObjectId(news._id);
              const { title, description, coverImg, author, authorPic, authorDesignation, isHeroBanner } = news;
              const truncatedDescription = stripHtml(description).substring(0, 120) + "...";

              return (
                <div key={_id} className="news-card">
                  {/* Card Image */}
                  <div className="news-card-image-wrapper">
                    <img
                      src={coverImg || "/assets/unnamed.jpeg"}
                      alt={title}
                      className="news-card-image"
                      onError={(e) => { e.target.src = "/assets/unnamed.jpeg"; }}
                    />
                    <div className="news-card-image-overlay"></div>
                    {isHeroBanner && (
                      <div className="news-card-hero-badge">
                        <FaStar /> Homepage Main
                      </div>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="news-card-body">
                    <h3 className="news-card-title">{title}</h3>
                    <p className="news-card-description">{truncatedDescription}</p>

                    {/* Author */}
                    <div className="news-card-author">
                      {authorPic ? (
                        <img
                          src={authorPic}
                          alt={author}
                          className="news-card-author-image"
                          onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div
                        className="news-card-author-placeholder"
                        style={{ display: authorPic ? 'none' : 'flex' }}
                      >
                        <FaUser />
                      </div>
                      <div className="news-card-author-info">
                        <p className="news-card-author-name">{author || 'Unknown Author'}</p>
                        {authorDesignation && (
                          <p className="news-card-author-designation">{authorDesignation}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Hero Banner Toggle */}
                  <div className="news-card-toggle">
                    <div
                      className={`news-card-toggle-switch ${isHeroBanner ? 'active' : ''}`}
                      onClick={() => handleHeroBannerToggle(_id, isHeroBanner)}
                    ></div>
                    <span className="news-card-toggle-label">Homepage Feature</span>
                  </div>

                  {/* Actions */}
                  <div className="news-card-actions">
                    <button
                      className="news-card-action-btn news-card-action-btn-view"
                      onClick={() => navigateToNewsDetails(_id)}
                    >
                      <FaEye /> View
                    </button>
                    <button
                      className="news-card-action-btn news-card-action-btn-edit"
                      onClick={() => setNewsPriority(news)}
                    >
                      <FaEdit /> Edit
                    </button>
                    <button
                      className="news-card-action-btn news-card-action-btn-delete"
                      onClick={() => confirmDelete(_id)}
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        className="news-delete-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this news article? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <button
            className="news-modal-btn-cancel"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            className="news-modal-btn-delete"
            onClick={handleOnDeleteNews}
          >
            Delete
          </button>
        </Modal.Footer>
      </Modal>

      <EditNewsModal
        show={showNewsEditModal}
        handleClose={() => setNewsEditModal(false)}
        newsDetails={newsEditData}
      />
    </CommonLayout>
  );
}

export default NewsPage;
